import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CalendarRange, ShieldCheck, Repeat2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/browser-client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Sign in | Leadership Edge Scheduling Console" },
      {
        name: "description",
        content:
          "Sign in to the Leadership Edge scheduling console to build course schedules, assign trainers, and deploy sessions.",
      },
      { property: "og:title", content: "Sign in | Leadership Edge Scheduling Console" },
      {
        property: "og:description",
        content: "Cadence-driven course scheduling for Leadership Edge admins and trainers.",
      },
    ],
  }),
  component: Landing,
});

const highlights = [
  {
    icon: Repeat2,
    title: "Cadence in, calendar out",
    body: "Each course carries its own cadence. The builder turns that into dated sessions across the timeframe you pick.",
  },
  {
    icon: CalendarRange,
    title: "Teach blocks respected",
    body: "Sessions only land on trainers who are authorized and free during one of their standing teaching blocks.",
  },
  {
    icon: ShieldCheck,
    title: "Deploy once",
    body: "One action pushes the catalog to the website, builds classes in Training Pipeline, and sends trainer invites.",
  },
];

function Landing() {
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const deniedValue = params.get("denied");
    const blocked = deniedValue?.replace(/^['"]|['"]$/g, "") === "1";
    setDenied(blocked);
    if (blocked || window.location.search.includes("denied")) return;

    let done = false;
    const go = () => {
      if (done || window.location.pathname !== "/" || window.location.search.includes("denied")) return;
      done = true;
      navigate({ to: "/dashboard", replace: true });
    };

    // The managed Google broker returns full-page sign-ins as #access_token=...
    // hash tokens; the PKCE client ignores those, so adopt them explicitly.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const access_token = hash.get("access_token");
    const refresh_token = hash.get("refresh_token");
    if (access_token && refresh_token) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      supabase.auth
        .setSession({ access_token, refresh_token })
        .then(({ data, error }) => {
          if (error) toast.error("Sign-in didn't complete. Please try again.");
          else if (data.session) go();
        });
    } else {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) go();
      });
    }

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && !window.location.search.includes("denied")) go();
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function signIn() {
    setBusy(true);
    try {
      // Lovable preview runs framed and Google refuses to render in an iframe, so
      // the Lovable Cloud preview uses the managed popup flow. AWS production
      // (self-hosted Supabase) keeps the plain redirect flow.
      const { readPublicSupabaseConfig } = await import("@/integrations/supabase/runtime-config");
      const source = readPublicSupabaseConfig()?.source;

      if (source === "lovable-cloud") {
        const { createLovableAuth } = await import("@lovable.dev/cloud-auth-js");
        const result = await createLovableAuth().signInWithOAuth("google", {
          redirect_uri: window.location.origin,
        });
        if (result.error) {
          setBusy(false);
          toast.error("Sign-in didn't complete. Please try again.");
          return;
        }
        if (result.redirected) return;
        // Set the session on the app's own client so it lands in the storage key
        // this app reads (the generated client uses a different one).
        await supabase.auth.setSession(result.tokens);
        navigate({ to: "/dashboard", replace: true });
        return;
      }


      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        setBusy(false);
        toast.error("Sign-in didn't complete. Please try again.");
      }
    } catch {
      setBusy(false);
      toast.error("Sign-in didn't complete. Please try again.");
    }
  }



  return (
    <main className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 bg-hero-wash" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-grid-lines opacity-40" aria-hidden="true" />

      <div className="relative mx-auto grid min-h-screen w-full max-w-6xl items-center gap-16 px-6 py-16 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Leadership Edge · Internal
          </span>
          <h1 className="mt-7 font-display text-5xl leading-[1.05] tracking-tight text-foreground sm:text-6xl">
            Course scheduling
            <span className="block text-accent">console</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Build a term of course sessions from cadence and trainer teaching blocks, review deviations, then deploy to
            the website, Training Pipeline, and trainer calendars.
          </p>

          {denied ? (
            <div className="mt-8 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground">
              That account isn't approved for this tool. Access is limited to lookingforwardconsulting.com addresses
              unless an admin adds an exception.
            </div>
          ) : null}

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button variant="brass" size="lg" onClick={signIn} disabled={busy} aria-busy={busy}>
              {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              {busy ? "Opening Google…" : "Sign in with Google"}
            </Button>
            <p className="text-sm text-muted-foreground">Admins and trainers use the same door.</p>
          </div>


          <dl className="mt-14 grid gap-6 sm:grid-cols-3">
            {highlights.map((item) => (
              <div key={item.title}>
                <item.icon className="h-5 w-5 text-accent" aria-hidden="true" />
                <dt className="mt-3 text-sm font-semibold text-foreground">{item.title}</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{item.body}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="rounded-2xl border border-border bg-card p-7 shadow-panel">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Sample build</p>
          <p className="mt-3 font-display text-2xl text-foreground">Q4 2026</p>
          <ul className="mt-6 space-y-4 text-sm">
            {[
              ["Leading Change", "Oct 6", "A. Rivera"],
              ["Psychological Safety", "Oct 20", "M. Osei"],
              ["Conquering Conflict", "Nov 3", "Unassigned"],
              ["4P Series: Improve Communication through the 4P Social Styles", "Nov 17", "J. Lindqvist"],
              ["Delegating for Development", "Dec 1", "A. Rivera"],
            ].map(([course, dates, trainer]) => (
              <li key={`${course}-${dates}`} className="flex items-baseline justify-between gap-4 border-b border-border pb-3 last:border-0">
                <div>
                  <p className="font-medium text-foreground">{course}</p>
                  <p className="font-mono text-xs text-muted-foreground">{dates}</p>
                </div>
                <span
                  className={
                    trainer === "Unassigned"
                      ? "rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive"
                      : "rounded-full bg-accent/12 px-2.5 py-1 text-xs font-medium text-accent"
                  }
                >
                  {trainer}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </main>
  );
}
